// src/components/cards/shared/dialogs/convert/ConvertPetSection.tsx
//
// One instance per sketch pet. Uses the same primitives as PetForm
// (CustomSelect, RadioButtonGroup, StyledSwitch, DateRangePicker).
//
// Required: name, type, size, sex. Optional: breed, colour, dob,
// inoculation date, sterilised (default No), stillOwned (default Yes).
// Breed/size/colour selects are gated on petType (matching PetForm).

import React from 'react';
import { Box } from '@mui/material';
import {
  FormFieldRow,
  FieldLabelContainer,
  FieldLabel,
  FieldInputContainer,
  StyledTextField,
  StyledSwitch,
  RadioButtonGroup,
  SectionHeaderWrapper,
  SectionHeader,
  FieldLabelContainerMultiline,
} from '../../../../forms/shared';
import CustomSelect from '../../../../common/CustomSelect';
import CustomSelectMenuItem from '../../../../common/CustomSelectMenuItem';
import DateRangePicker from '../../../../forms/shared/DateRangePicker';
import { getCustomIcon } from '../../../../../utils/customIcons';
import {
  PetType,
  PetSize,
  PetBreed,
  PetColour,
} from '../../../../../services/api';

export interface ConvertPetFormState {
  name: string;
  petTypeId: string;
  petSizeId: string;
  petBreedId: string;
  petColourId: string;
  gender: string;
  dateOfBirth: string;
  inoculationDate: string;
  sterilised: boolean;
  stillOwned: boolean;
}

export interface ConvertPetFormErrors {
  name?: string;
  petTypeId?: string;
  petSizeId?: string;
  gender?: string;
}

interface ConvertPetSectionProps {
  petId: number;
  originalSketchName: string;
  originalPetTypeName: string;
  value: ConvertPetFormState;
  errors: ConvertPetFormErrors;
  isSubmitting: boolean;
  petTypes: PetType[];
  petSizes: PetSize[];
  petBreeds: PetBreed[];
  petColours: PetColour[];
  onChange: (next: ConvertPetFormState) => void;
  // DOM id for scroll-to-first-error.
  id?: string;
}

const ConvertPetSection: React.FC<ConvertPetSectionProps> = ({
  petId,
  originalSketchName,
  originalPetTypeName,
  value,
  errors,
  isSubmitting,
  petTypes,
  petSizes,
  petBreeds,
  petColours,
  onChange,
  id,
}) => {
  const update = <K extends keyof ConvertPetFormState>(
    key: K,
    next: ConvertPetFormState[K]
  ) => {
    onChange({ ...value, [key]: next });
  };

  // Cascading selects: type -> size, size -> breed, type -> colour.
  // Filter the available options down by the current selections.
  const typeIdNum = value.petTypeId ? parseInt(value.petTypeId, 10) : null;
  const sizeIdNum = value.petSizeId ? parseInt(value.petSizeId, 10) : null;

  const filteredSizes = typeIdNum
    ? petSizes.filter((s) => s.petTypeFk === typeIdNum)
    : [];

  const filteredBreeds = sizeIdNum
    ? petBreeds.filter((b) => b.petSizeFk === sizeIdNum)
    : [];

  const filteredColours = typeIdNum
    ? petColours.filter((c) => c.petType === typeIdNum)
    : [];

  // Cascading reset: clearing a parent selection blanks the dependents.
  const handleTypeChange = (next: string) => {
    onChange({
      ...value,
      petTypeId: next,
      petSizeId: '',
      petBreedId: '',
      petColourId: '',
    });
  };

  const handleSizeChange = (next: string) => {
    onChange({
      ...value,
      petSizeId: next,
      petBreedId: '',
    });
  };

  return (
    <Box id={id}>
      {/* Section header bar with pet icon + current/original name */}
      <SectionHeaderWrapper
        sx={{ '@media (max-width: 600px)': { pt: '0.5rem' } }}
      >
        <FieldLabelContainerMultiline
          sx={{
            paddingTop: '0.2rem',
            '@media (max-width: 600px)': { display: 'none' },
          }}
        />
        <SectionHeader
          sx={{
            '& > p': { fontSize: '2rem' },
            '@media (max-width: 600px)': { pl: 0 },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', pl: '0.5rem' }}>
            {getCustomIcon(originalPetTypeName, '2rem')}
          </Box>
          <FieldLabel>
            {value.name?.trim() || originalSketchName || `Pet #${petId}`}
          </FieldLabel>
        </SectionHeader>
      </SectionHeaderWrapper>

      {/* Pet Name * (this IS the rename input - no separate rename row) */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>
            Pet Name<span className="required">*</span>
          </FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <StyledTextField
            fullWidth
            value={value.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update('name', e.target.value)
            }
            error={Boolean(errors.name)}
            helperText={errors.name}
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Type * */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>
            Type<span className="required">*</span>
          </FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <CustomSelect
            value={value.petTypeId}
            onChange={handleTypeChange}
            ariaLabel="Type"
            label=""
            placeholder="Select type"
            disabled={isSubmitting}
            error={Boolean(errors.petTypeId)}
            helperText={errors.petTypeId}
            width="100%"
            maxWidth="600px"
          >
            {petTypes.map((t) => (
              <CustomSelectMenuItem key={t.id} value={t.id.toString()}>
                {t.type}
              </CustomSelectMenuItem>
            ))}
          </CustomSelect>
        </FieldInputContainer>
      </FormFieldRow>

      {/* Size * */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>
            Size<span className="required">*</span>
          </FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <CustomSelect
            value={value.petSizeId}
            onChange={handleSizeChange}
            ariaLabel="Size"
            label=""
            placeholder={typeIdNum ? 'Select size' : 'Pick a type first'}
            disabled={isSubmitting || !typeIdNum}
            error={Boolean(errors.petSizeId)}
            helperText={errors.petSizeId}
            width="100%"
            maxWidth="600px"
          >
            {filteredSizes.map((s) => (
              <CustomSelectMenuItem key={s.id} value={s.id.toString()}>
                {s.size}
              </CustomSelectMenuItem>
            ))}
          </CustomSelect>
        </FieldInputContainer>
      </FormFieldRow>

      {/* Breed (optional) */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>Breed</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <CustomSelect
            value={value.petBreedId}
            onChange={(next) => update('petBreedId', next)}
            ariaLabel="Breed"
            label=""
            placeholder={
              sizeIdNum ? 'Select breed (optional)' : 'Pick a size first'
            }
            disabled={isSubmitting || !sizeIdNum}
            width="100%"
            maxWidth="600px"
          >
            <CustomSelectMenuItem value="">(none)</CustomSelectMenuItem>
            {filteredBreeds.map((b) => (
              <CustomSelectMenuItem key={b.id} value={b.id.toString()}>
                {b.breed}
              </CustomSelectMenuItem>
            ))}
          </CustomSelect>
        </FieldInputContainer>
      </FormFieldRow>

      {/* Sex * */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>
            Sex<span className="required">*</span>
          </FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <Box>
            <RadioButtonGroup
              value={value.gender}
              onChange={(next) => update('gender', next)}
              options={[
                { value: 'Male', label: 'Male' },
                { value: 'Female', label: 'Female' },
              ]}
              error={Boolean(errors.gender)}
              helperText={errors.gender}
              disabled={isSubmitting}
              direction="horizontal"
              name={`pet-gender-${petId}`}
              required
            />
          </Box>
        </FieldInputContainer>
      </FormFieldRow>

      {/* Colour (optional) */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>Colour</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <CustomSelect
            value={value.petColourId}
            onChange={(next) => update('petColourId', next)}
            ariaLabel="Colour"
            label=""
            placeholder={
              typeIdNum ? 'Select colour (optional)' : 'Pick a type first'
            }
            disabled={isSubmitting || !typeIdNum}
            width="100%"
            maxWidth="600px"
          >
            <CustomSelectMenuItem value="">(none)</CustomSelectMenuItem>
            {filteredColours.map((c) => (
              <CustomSelectMenuItem key={c.id} value={c.id.toString()}>
                {c.colour}
              </CustomSelectMenuItem>
            ))}
          </CustomSelect>
        </FieldInputContainer>
      </FormFieldRow>

      {/* Date of Birth */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>Date of Birth</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <DateRangePicker
            mode="single"
            desktopType="modal"
            selectedDate={value.dateOfBirth}
            onDateChange={(next) => update('dateOfBirth', next)}
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Inoculation Date */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>Inoc. Date</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <DateRangePicker
            mode="single"
            desktopType="modal"
            selectedDate={value.inoculationDate}
            onDateChange={(next) => update('inoculationDate', next)}
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Sterilised (default No) */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>Sterilised</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <Box display="flex" alignItems="center">
            <StyledSwitch
              checked={value.sterilised}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                update('sterilised', e.target.checked)
              }
              disabled={isSubmitting}
              showLabels
              trueLabel="Yes"
              falseLabel="No"
            />
          </Box>
        </FieldInputContainer>
      </FormFieldRow>

      {/* Still Owned (default Yes) */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>Still Owned</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <Box display="flex" alignItems="center">
            <StyledSwitch
              checked={value.stillOwned}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                update('stillOwned', e.target.checked)
              }
              disabled={isSubmitting}
              showLabels
              trueLabel="Yes"
              falseLabel="No"
            />
          </Box>
        </FieldInputContainer>
      </FormFieldRow>
    </Box>
  );
};

export default ConvertPetSection;
